/**
 * Loading one person for the Edit user page and saving everything on it in one go. Built only on the routes that
 * exist (People, team per project, users); there is no single-person route, so the person is found in the list.
 *
 * Saving never stops at the first failure and never hides one: every change is tried, each failure is kept against
 * the part of the page it belongs to (details, login role, or one project line), and the caller reloads afterwards.
 */
import { fetchPeople, updatePerson, addPersonToProject, removePersonFromProject } from '../../services/PeopleService';
import { changeLoginRole, fetchProjectRoster, updateMembership } from '../../services/PersonMembershipService';
import { errorMessageOf } from '../../utils/apiErrors';
import { knownTeams } from './peopleView';
import { linesFromPerson, type LineChanges, type RoleLine } from './personLines';
import type { TeamMember } from '../../types/Team';
import type { UserRole } from '../../types/User';
import type { Person, PersonInput } from '../../types/People';

export interface LoadedPerson {
  person: Person;
  lines: RoleLine[];
  /** Team names already used in the workspace, offered as suggestions. */
  teams: string[];
}

/** The person with this id, live or archived, plus the live list they came from (for the team suggestions). */
export async function findPerson(personId: string): Promise<{ person: Person | null; live: Person[] }> {
  const live = await fetchPeople('active');
  const person = live.find(p => p.id === personId) ?? (await fetchPeople('archived')).find(p => p.id === personId) ?? null;
  return { person, live };
}

/** The person with this id (live or archived) and their project lines; null when the workspace has no such person. */
export async function loadPerson(personId: string): Promise<LoadedPerson | null> {
  const { person, live } = await findPerson(personId);
  if (!person) return null;
  const rosters = new Map<string, readonly TeamMember[]>();
  await Promise.all(person.projects.map(async pr => {
    try { rosters.set(pr.project_id, await fetchProjectRoster(pr.project_id)); } catch { /* the line says so */ }
  }));
  return { person, lines: linesFromPerson(person, rosters), teams: knownTeams([...live, person]) };
}

export interface SaveRequest {
  personId: string;
  /** Only the fields that changed; empty when the details are untouched. */
  details: Partial<PersonInput>;
  /** The new login role, or null when it is not being changed. */
  loginRole: { userId: string; role: UserRole } | null;
  changes: LineChanges;
}

export interface SaveResult {
  detailsError: string | null;
  roleError: string | null;
  /** By line key as it will be after the reload (a new line that was created uses its team entry id). */
  lineErrors: Record<string, string>;
  /** Keys of lines whose removal failed. */
  failedRemovals: Set<string>;
  /** How many changes went through. */
  saved: number;
  failed: number;
}

const idOf = (made: unknown): string | null => {
  const id = (made as { id?: unknown } | null)?.id;
  return typeof id === 'string' && id ? id : null;
};

export async function savePersonChanges(req: SaveRequest): Promise<SaveResult> {
  const result: SaveResult = { detailsError: null, roleError: null, lineErrors: {}, failedRemovals: new Set(), saved: 0, failed: 0 };
  const ok = () => { result.saved += 1; };
  const bad = () => { result.failed += 1; };

  if (Object.keys(req.details).length > 0) {
    try { await updatePerson(req.personId, req.details); ok(); }
    catch (err) { result.detailsError = errorMessageOf(err, "Couldn't save the person's details."); bad(); }
  }
  if (req.loginRole) {
    try { await changeLoginRole(req.loginRole.userId, req.loginRole.role); ok(); }
    catch (err) { result.roleError = errorMessageOf(err, "Couldn't change the login's role."); bad(); }
  }

  for (const line of req.changes.remove) {
    try { await removePersonFromProject(req.personId, line.projectId); ok(); }
    catch (err) {
      result.failedRemovals.add(line.key);
      result.lineErrors[line.key] = errorMessageOf(err, `Couldn't take them off ${line.projectName}.`);
      bad();
    }
  }
  for (const { line, patch } of req.changes.update) {
    try { await updateMembership(line.projectId, line.teamMemberId as string, patch); ok(); }
    catch (err) { result.lineErrors[line.key] = errorMessageOf(err, `Couldn't save the role on ${line.projectName}.`); bad(); }
  }
  for (const line of req.changes.create) {
    let created: string | null = null;
    try {
      created = idOf(await addPersonToProject(req.personId, line.projectId, line.teams));
    } catch (err) {
      result.lineErrors[line.key] = errorMessageOf(err, `Couldn't add them to ${line.projectName}.`);
      bad();
      continue;
    }
    const extra = {
      ...(line.role.trim() ? { role: line.role.trim() } : {}),
      ...(line.responsibilities.trim() ? { responsibilities: line.responsibilities } : {}),
      ...(line.isActive ? {} : { is_active: false }),
    };
    if (Object.keys(extra).length === 0) { ok(); continue; }
    try {
      if (!created) throw new Error('no id');
      await updateMembership(line.projectId, created, extra);
      ok();
    } catch (err) {
      // Added, but the role details did not go through: the line stays, now tied to the saved entry.
      result.lineErrors[created ?? line.key] = `Added to ${line.projectName}, but the role details were not saved. ${errorMessageOf(err, 'Please save again.')}`;
      bad();
    }
  }
  return result;
}
